// Gate 3.4 · hands an incoming ride call from the global listener to RideCall without redialling.
// pending: rideId -> { rideId, callId, from, offer, ice: [], at, autoAccept }
export const RIDE_PENDING_MS = 50000;
const pending = new Map();
const mounted = new Map();
const subs = new Set();
let sender = null;

const emit = () => subs.forEach((fn) => fn());
const fresh = (p) => p && Date.now() - p.at < RIDE_PENDING_MS;

export const rideCallStore = {
  subscribe(fn) { subs.add(fn); return () => subs.delete(fn); },
  setSender(fn) { sender = fn; },
  send(msg) { return sender ? sender(msg) : false; },
  mount(rideId) {
    mounted.set(rideId, (mounted.get(rideId) || 0) + 1);
    emit();
    return () => { const n = (mounted.get(rideId) || 1) - 1; n ? mounted.set(rideId, n) : mounted.delete(rideId); emit(); };
  },
  isMounted: (rideId) => mounted.has(rideId),
  // Returns true only for a new call (duplicate deliveries of the same offer are ignored).
  offer({ rideId, callId, from, offer }) {
    if (!rideId || !callId) return false;
    const cur = pending.get(rideId);
    if (cur && cur.callId === callId) return false;
    if ([...pending.values()].some((p) => fresh(p) && p.rideId !== rideId)) return false;   // one banner at a time
    pending.set(rideId, { rideId, callId, from, offer, ice: [], at: Date.now(), autoAccept: false });
    emit();
    return true;
  },
  addIce(rideId, callId, candidate) {
    const p = pending.get(rideId);
    if (p && p.callId === callId && candidate) p.ice.push(candidate);
  },
  markAnswer(rideId, callId) {
    const p = pending.get(rideId);
    if (!p || p.callId !== callId || !fresh(p)) return false;
    p.autoAccept = true;
    emit();
    return true;
  },
  // Trip page unmounted before answering (StrictMode remount or navigation): hand the call back to the banner.
  release(rideId, p) {
    if (pending.get(rideId) !== p) return;
    p.taken = false;                                                 // keeps autoAccept: the user already chose Answer
    emit();
  },
  clear(rideId, callId) {
    const p = pending.get(rideId);
    if (!p || (callId && p.callId !== callId)) return;
    pending.delete(rideId);
    emit();
  },
  clearAll() { if (pending.size) { pending.clear(); emit(); } },
  // Banner shows the first fresh call whose trip page is not already handling it.
  current() {
    for (const p of pending.values()) {
      if (!fresh(p)) { pending.delete(p.rideId); continue; }
      if (!p.taken && !mounted.has(p.rideId)) return p;
    }
    return null;
  },
  // Trip page adopts the call; the entry stays (taken) so ICE arriving before its own socket opens is kept.
  take(rideId) {
    const p = pending.get(rideId);
    if (!fresh(p) || p.taken) return null;
    p.taken = true;
    emit();
    return p;
  },
};
